/* eslint-disable id-length */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  JSONSchemaForBuildkitePipelineConfigurationFiles as Pipeline,
  CommandStep,
  WaitStep,
  BlockStep,
  StringBlockStep,
  StringWaitStep,
} from "./schema";
import { Node, Edge, Position, XYPosition } from "reactflow";
import schema from "./schema.json";
import { Validator, Schema } from "jsonschema";
import YAML from "yaml";

export type Step = Pipeline["steps"][number];

export type StepNode = Node<Step> & {
  type: NodeType;
};

export enum NodeType {
  Root = "root",
  Block = "block",
  Wait = "wait",
  Command = "command",
  Unknown = "unknown",
}

export enum EdgeType {
  Implicit = "implicit",
  Explicit = "explicit",
}

interface Graph {
  nodes: Node[];
  edges: Edge[];
}

let nodeCounter = 0;
function generateNodeID(): string {
  nodeCounter++;
  return `${nodeCounter}`;
}

function initialiseNodeIDs() {
  nodeCounter = 0;
}

/**
 * Parse and validate pipeline YAML against the schema
 */
export function parse(yaml: string): Pipeline {
  const pipeline = YAML.parse(yaml);
  new Validator().validate(pipeline, schema as Schema, {
    throwError: true,
  });

  return pipeline as Pipeline;
}

/**
 * Transform pipeline into a ReactFlow graph
 *
 * // TODO: validate against circular references
 */
export function toGraph(config: Pipeline): Graph {
  initialiseNodeIDs();

  let nodes: StepNode[] = [createRootNode()];

  config.steps.forEach((step: Step) => {
    const node = createNode(step, { x: 0, y: 0 });

    if (node) {
      nodes = [...nodes, node];
    }
  });

  let edges = createEdgesForExplicitDependencies(nodes);
  edges = createEdgesForImplicitDependencies(nodes, edges);
  edges = createEdgesForRootNode(nodes, edges);

  // Remove edges that are transitively implied by other edges
  edges = transitiveReduction(edges);

  return {
    nodes,
    edges,
  };
}

// Take an array of edges and remove any that are transitively implied by other edges.
//
// https://en.wikipedia.org/wiki/Transitive_reduction
//
// This is a port of the algorithm described here:
// https://www.cs.tufts.edu/comp/150FP/archive/al-aho/transitive-reduction.pd
//
function transitiveReduction(edges: Edge[]): Edge[] {
  type AdjacencyMap = Map<string, Map<string, Edge>>;

  const graph: AdjacencyMap = new Map();

  // Build an adjacency map of the graph to make it easier to traverse.
  for (const edge of edges) {
    const { source, target } = edge;
    if (!graph[source]) {
      graph[source] = new Map();
    }
    graph[source][target] = edge;
  }

  // Depth-first search with a callback for each vertex visited.
  //
  // If the callback returns true, the search is terminated early.
  //
  function dfs(
    graph: AdjacencyMap,
    start: string,
    visit: (vertex: string) => boolean | undefined,
  ) {
    const stack: string[] = [start];
    const visited = new Set();

    while (stack.length) {
      const vertex = stack.pop();

      if (vertex && !visited.has(vertex)) {
        if (visit(vertex)) {
          return;
        }

        visited.add(vertex);

        for (const neighbor of graph[vertex]) {
          stack.push(neighbor);
        }
      }
    }
  }

  let output: Edge[] = [...edges];

  // Iterate over all direct successors of all vertices in the graph, and start a
  // depth-first search from each successor.
  //
  // For each vertex visited in the DFS, take a look at its direct successors.
  // If there is an edge between the top-level iteration vertex and the successor
  // of the current node, this edge can be removed.
  //
  for (const vertex in graph) {
    for (const successor in graph[vertex]) {
      dfs(graph, successor, (current: string) => {
        if (!graph[current]) {
          return true;
        }

        for (const edge in graph[current]) {
          if (graph[vertex][edge]) {
            const e = graph[vertex][edge];
            output = output.filter((edge) => edge.id !== e.id);
          }
        }
      });
    }
  }

  return output;
}

/**
 * Create edges from the root node to all other nodes.
 *
 * @param nodes
 * @param edges
 * @returns
 */
function createEdgesForRootNode(nodes: StepNode[], edges: Edge[]): Edge[] {
  const [rootNode, ...rest] = nodes;
  const output: Edge[] = [...edges];

  rest.every((node) => {
    // Everything past the first wait or block step is implicitly dependent on that node.
    if (node.type === NodeType.Wait || node.type === NodeType.Block) {
      return false;
    }

    // If there's no edges coming into the node, create one from the root node.
    if (!edges.find((edge) => edge.target === node.id)) {
      const edge = createEdge(rootNode.id, node.id, EdgeType.Implicit);
      output.push(edge);
    }

    return true;
  });

  return output;
}

/**
 * Wait and block steps provide implicit dependencies.
 *
 * - A wait or block step, is dependent on all previous steps completing successfully;
 * - All steps following up until the next wait or block, are dependent on the step;
 *
 * @see https://buildkite.com/docs/pipelines/dependencies#implicit-dependencies-with-wait-and-block
 */
function createEdgesForImplicitDependencies(
  nodes: StepNode[],
  edges: Edge[],
): Edge[] {
  let output: Edge[] = [...edges];

  // Find all the wait/block steps
  const waitNodes = nodes.filter(
    (node) => node.type === NodeType.Wait || node.type === NodeType.Block,
  );

  // Create edges for every node _before_ each wait/block node.
  waitNodes.reduce((fromIndex, waitNode) => {
    const idx = nodes.indexOf(waitNode);
    const implicitDeps = nodes.slice(fromIndex, idx);

    implicitDeps.forEach((node) => {
      if (node.type === NodeType.Root) {
        return;
      }

      const edge = createEdge(node.id, waitNode.id, EdgeType.Implicit);
      output = [...output, edge];
    });

    return idx + 1;
  }, 0);

  // Create edges for every node _after_ each wait/block node.
  waitNodes.reduceRight((fromIndex, waitNode) => {
    const idx = nodes.indexOf(waitNode) + 1;
    const implicitDeps = nodes.slice(idx, fromIndex);

    implicitDeps.forEach((node) => {
      const edge = createEdge(waitNode.id, node.id, EdgeType.Implicit);
      output = [...output, edge];
    });

    return idx;
  }, nodes.length);

  return output;
}

/**
 * Explicit dependencies are defined using the `depends_on` key.
 *
 * - depends_on can be either a string;
 * - an array of strings;
 * - or an array of objects with `step` keys
 *
 * @see https://buildkite.com/docs/pipelines/dependencies#defining-explicit-dependencies
 */
function createEdgesForExplicitDependencies(nodes: StepNode[]): Edge[] {
  let edges: Edge[] = [];

  function findNodeByKey(key: string): StepNode {
    const node = nodes.find((node) => (node.data as any).key === key);
    if (!node) {
      throw new Error(`Invalid step key: ${key}`);
    }
    return node;
  }

  nodes
    .filter((node) => (node.data as any).depends_on)
    .forEach((node) => {
      const step = node.data as any;
      let dependencies: Node[] = [];

      if (typeof step.depends_on === "string") {
        const dependecy = findNodeByKey(step.depends_on);
        dependencies = [dependecy];
      }

      if (Array.isArray(step.depends_on)) {
        dependencies = step.depends_on.map((dependency) =>
          findNodeByKey(dependency),
        );
      }

      edges = [
        ...edges,
        ...dependencies.map((dependency) =>
          createEdge(dependency.id, node.id, EdgeType.Explicit),
        ),
      ];
    });

  return edges;
}

/**
 * Create a ReactFlow edge
 */
function createEdge(from: string, to: string, type: EdgeType): Edge {
  return {
    id: `${from}-${to}`,
    source: from,
    target: to,
    type,
  };
}

function createRootNode(): StepNode {
  return {
    id: "root",
    type: NodeType.Root,
    position: { x: 0, y: 0 },
    data: {},
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  };
}

/**
 * Create a ReactFlow node from a pipeline step
 */
function createNode(step: Step, position: XYPosition): StepNode | null {
  const type = getNodeType(step);
  if (type === NodeType.Unknown) {
    console.error(`Unknown node type for step:`, step);
  }

  return {
    // Give each node a unique ID
    id: generateNodeID(),
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    position,
    data: step,
    type,
  };
}

// Infer node type from the step object
export function getNodeType(step: Step): NodeType {
  if (isWaitStep(step) || isStringWaitStep(step)) {
    return NodeType.Wait;
  }

  if (isBlockStep(step) || isStringBlockStep(step)) {
    return NodeType.Block;
  }

  if (isCommandStep(step)) {
    return NodeType.Command;
  }

  return NodeType.Unknown;
}

function isStringBlockStep(step: Step): step is StringBlockStep {
  return step === "block";
}

function isBlockStep(step: Step): step is BlockStep {
  return (step as any).type === "block" || (step as any).block !== undefined;
}

function isStringWaitStep(step: Step): step is StringWaitStep {
  return step === "wait";
}

function isWaitStep(step: Step): step is WaitStep {
  return (step as any).type === "wait" || (step as any).wait !== undefined;
}

function isCommandStep(step: Step): step is CommandStep {
  return (
    (step as any).type === "command" ||
    (step as any).commands !== undefined ||
    (step as any).command !== undefined ||
    (step as any).plugins !== undefined
  );
}
